import { useEffect } from "react";
import type { Editor } from "@progress/kendo-react-editor";
import { handleEditorError } from "./editor-utils";
import { isColorPickerElement } from "../utils/domHelpers";

/**
 * Custom hook for handling color picker interactions
 * Enhanced with error handling for event listener management
 * Now using extracted DOM helper utilities for better maintainability
 */
export const useColorPickerEvents = (
  editorRef: React.RefObject<Editor>,
  isDisabled: boolean,
  focusEditor: () => void
) => {
  useEffect(() => {
    if (!editorRef.current || isDisabled) return;

    const handleColorPickerClose = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target && isColorPickerElement(target)) {
        focusEditor();
      }
    };

    try {
      // Listen for clicks on the document to catch color picker interactions
      document.addEventListener("click", handleColorPickerClose);

      return () => {
        try {
          document.removeEventListener("click", handleColorPickerClose);
        } catch (error) {
          handleEditorError(error as Error, "color picker event cleanup");
        }
      };
    } catch (error) {
      handleEditorError(error as Error, "color picker event setup");
      return undefined;
    }
  }, [editorRef, isDisabled, focusEditor]);
};
